'use client'

import React, { useEffect, useState } from 'react'
import { createClient } from '../lib/supabase/client'
import { siteUrl } from '../lib/site-url'
import { clearOAuthAttempt, markOAuthAttempt } from '../lib/inAppBrowser'
import { authFailReason } from '../lib/auth-reason'
import { PillButton } from './ui'
import BrandIcon from './BrandIcon'

type Provider = 'google' | 'github'

// Social sign-in for /login and /signup. Uses Supabase OAuth (authorization-code
// flow), so each provider lands back on /auth/callback with ?code=, which the
// callback route exchanges for a session. Renders Google + GitHub — the two
// providers wired in Supabase (GitHub is the developer-first wedge).
//
// New design: each is a pill (label left, circular white badge holding the
// provider mark on the right). Stacks full-width on mobile; the caller decides
// the row vs. column layout via `layout`.
export default function OAuthButtons({
  next = '/dashboard',
  verb = 'start with',
  layout = 'column',
  className,
  onError,
}: {
  next?: string
  verb?: string
  /** 'column' = stacked (mobile + default), 'row' = side-by-side (desktop). */
  layout?: 'row' | 'column'
  /** extra class on the row — used by the auth surfaces to carry `.auth-turn`
   *  when the door changes its mind about which verb these doors are wearing,
   *  without adding a wrapper that would change the column's own spacing. */
  className?: string
  onError?: (msg: string) => void
}) {
  // row → responsive (side-by-side on desktop, stacked on mobile) via .oauth-row;
  // column → always stacked.
  return (
    <div
      className={[layout === 'row' ? 'oauth-row' : null, className].filter(Boolean).join(' ') || undefined}
      style={layout === 'row' ? undefined : { display: 'flex', flexDirection: 'column', gap: '0.85rem' }}
    >
      <OAuthButton provider="google" label="Google" next={next} verb={verb} onError={onError} iconDelay={0.04} />
      <OAuthButton provider="github" label="Github" next={next} verb={verb} onError={onError} iconDelay={0.1} />
    </div>
  )
}

function OAuthButton({
  provider,
  label,
  next,
  verb,
  onError,
  iconDelay,
}: {
  provider: Provider
  label: string
  next: string
  verb: string
  onError?: (msg: string) => void
  iconDelay?: number
}) {
  const [loading, setLoading] = useState(false)
  // Back from the provider restores this page from the back/forward cache with
  // its state intact, pill still "connecting…" and disabled: the trip is over,
  // so the pill comes back to life
  useEffect(() => {
    const show = (e: PageTransitionEvent) => {
      if (e.persisted) setLoading(false)
    }
    window.addEventListener('pageshow', show)
    return () => window.removeEventListener('pageshow', show)
  }, [])
  // provider_blocked is a HARD refusal (the provider rejects this webview) — no
  // retry can ever succeed, so once it's seen the button stops attempting. One
  // observed storm: a single user re-tapping a silently-failing pill fired 77
  // auth_failed events in an hour. The wall gets said once, in our voice.
  const [blocked, setBlocked] = useState(false)

  // Report the failure ONCE, then close the door on retries. The human message
  // deliberately says "in-app browser" so authFailReason still files it under
  // provider_blocked in every caller's funnel event.
  const failed = (msg: string) => {
    if (authFailReason(msg) === 'provider_blocked') {
      setBlocked(true)
      onError?.(
        `${label} won't sign you in inside this app's in-app browser — open innernet.live in safari or chrome, or use the email option below, which works right here.`,
      )
      return
    }
    onError?.(msg)
  }

  const signIn = async () => {
    // the provider already refused this browser — a re-tap has nowhere to go
    if (blocked) return
    // The innernet desktop app is an embedded Chromium webview — Google (and
    // GitHub) refuse OAuth there. So when we're inside the app, hand the whole
    // provider flow to the SYSTEM browser via /auth/start, carrying source=app
    // so the finished session deep-links back into the app (/auth/handoff).
    const desktop =
      typeof window !== 'undefined'
        ? (window as unknown as { innernetDesktop?: { shell?: string; openExternal?: (u: string) => void } })
            .innernetDesktop
        : undefined
    if (desktop?.shell === 'desktop') {
      const url = `${siteUrl()}/auth/start?provider=${provider}&source=app`
      if (desktop.openExternal) desktop.openExternal(url)
      else window.open(url, '_blank', 'noopener')
      onError?.(`finishing ${label} sign-in in your browser — come back to the app when it's done.`)
      return
    }

    // ⚠️ WE DO NOT PRE-JUDGE AN IN-APP BROWSER. This used to short-circuit both
    // providers whenever detectInAppBrowser() was true, on the theory that
    // Google always refuses OAuth in a webview. It does not — the block varies
    // by host app, OS version and client, and a competitor's Google sign-in was
    // observed working inside X on iOS while ours refused to try. Guessing
    // turned a POSSIBLE failure into a CERTAIN one, and reported a 403 nobody
    // had seen. GitHub was collateral damage: it has no such policy on record
    // and was blocked purely by association.
    //
    // So: always attempt the real redirect. Leave a crumb first — if the user
    // comes back without a session, InAppBrowserNotice reads it and offers the
    // escape then, on evidence rather than on a hunch.
    markOAuthAttempt(label.toLowerCase())
    setLoading(true)
    try {
      const supabase = createClient()
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          // /auth/land, not the server callback: the implicit flow returns the
          // session in the URL FRAGMENT, and fragments never reach a server
          // route — only a client page can absorb them. /auth/land handles the
          // hash, a ?code= (older links), and error_description alike.
          redirectTo: `${siteUrl()}/auth/land?next=${encodeURIComponent(next)}`,
          // access_type/prompt are Google-specific (refresh-token consent); harmless to omit elsewhere
          ...(provider === 'google'
            ? { queryParams: { access_type: 'offline', prompt: 'consent' } }
            : {}),
        },
      })
      if (error) {
        // the redirect never happened, so there is nothing to come back from
        clearOAuthAttempt()
        setLoading(false)
        failed(error.message)
      }
      // on success the browser is redirected to the provider; no further work here
    } catch (e) {
      clearOAuthAttempt()
      setLoading(false)
      failed(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <PillButton
      type="button"
      variant="oauth"
      onClick={signIn}
      loading={loading}
      disabled={blocked}
      iconDelay={iconDelay}
      aria-label={blocked ? `${label} sign-in is blocked in this browser` : `${verb} ${label}`}
      icon={<BrandIcon name={provider} size={20} />}
    >
      {blocked ? 'blocked in this browser' : loading ? 'connecting…' : `${verb} ${label}`}
    </PillButton>
  )
}
